import { HEIGHT_SIDE, HEIGHT_SPACING, WORLD_HALF } from "./params.ts";
import type { TerrainField } from "./terrain.ts";

/**
 * Heightfield físico: alturas Float32 numa grade de 2 m. Cada quad (i, j) é dividido pela
 * diagonal de (i, j) a (i+1, j+1); a malha de maior detalhe usa exatamente essa diagonal, então
 * o pé do jogador coincide com o desenho (doc 07, A3).
 */
export interface Heightfield {
  side: number;
  spacing: number;
  heights: Float32Array;
}

export const heightIndex = (ix: number, iz: number): number => iz * HEIGHT_SIDE + ix;
export const heightCoord = (i: number): number => -WORLD_HALF + i * HEIGHT_SPACING;

/** Calcula as alturas de um retângulo de amostras [ix0, ix1) × [iz0, iz1) (útil para fatiar entre workers). */
export function fillHeights(field: TerrainField, target: Float32Array, iz0: number, iz1: number, ix0 = 0, ix1 = HEIGHT_SIDE): void {
  for (let iz = iz0; iz < iz1; iz++) {
    const z = heightCoord(iz);
    for (let ix = ix0; ix < ix1; ix++) target[iz * HEIGHT_SIDE + ix] = field.height(heightCoord(ix), z);
  }
}

export function buildHeightfield(field: TerrainField): Heightfield {
  const heights = new Float32Array(HEIGHT_SIDE * HEIGHT_SIDE);
  fillHeights(field, heights, 0, HEIGHT_SIDE);
  return { side: HEIGHT_SIDE, spacing: HEIGHT_SPACING, heights };
}

interface Cell {
  ix: number;
  iz: number;
  fx: number;
  fz: number;
}

function locate(x: number, z: number, out: Cell): Cell {
  const max = HEIGHT_SIDE - 1;
  let gx = (x + WORLD_HALF) / HEIGHT_SPACING;
  let gz = (z + WORLD_HALF) / HEIGHT_SPACING;
  gx = gx < 0 ? 0 : gx > max ? max : gx;
  gz = gz < 0 ? 0 : gz > max ? max : gz;
  let ix = Math.floor(gx);
  let iz = Math.floor(gz);
  if (ix >= max) ix = max - 1;
  if (iz >= max) iz = max - 1;
  out.ix = ix;
  out.iz = iz;
  out.fx = gx - ix;
  out.fz = gz - iz;
  return out;
}

const scratch: Cell = { ix: 0, iz: 0, fx: 0, fz: 0 };

/** Altura no ponto (x, z) por interpolação no triângulo da malha. Fora do mundo, satura na borda. */
export function sampleHeight(hf: Heightfield, x: number, z: number): number {
  const { ix, iz, fx, fz } = locate(x, z, scratch);
  const h = hf.heights;
  const i = iz * HEIGHT_SIDE + ix;
  const h00 = h[i] as number;
  const h11 = h[i + HEIGHT_SIDE + 1] as number;
  if (fx >= fz) {
    const h10 = h[i + 1] as number;
    return h00 + fx * (h10 - h00) + fz * (h11 - h10);
  }
  const h01 = h[i + HEIGHT_SIDE] as number;
  return h00 + fz * (h01 - h00) + fx * (h11 - h01);
}

/** Gradiente (dh/dx, dh/dz) do triângulo sob (x, z). Normal = normalize(−gx, 1, −gz). */
export function sampleGradient(hf: Heightfield, x: number, z: number, out = { x: 0, z: 0 }): { x: number; z: number } {
  const { ix, iz, fx, fz } = locate(x, z, scratch);
  const h = hf.heights;
  const i = iz * HEIGHT_SIDE + ix;
  const h00 = h[i] as number;
  const h11 = h[i + HEIGHT_SIDE + 1] as number;
  if (fx >= fz) {
    const h10 = h[i + 1] as number;
    out.x = (h10 - h00) / HEIGHT_SPACING;
    out.z = (h11 - h10) / HEIGHT_SPACING;
  } else {
    const h01 = h[i + HEIGHT_SIDE] as number;
    out.x = (h11 - h01) / HEIGHT_SPACING;
    out.z = (h01 - h00) / HEIGHT_SPACING;
  }
  return out;
}
